(function () {
  const STORAGE_KEY = 'theme:new-customer-coupon-claimed';
  const STORAGE_DISMISSED_KEY = 'theme:new-customer-coupon-dismissed';
  /** Stores hide-until timestamp (ms). Legacy value "true" is treated as expired. */
  const STORAGE_TEASER_HIDDEN_KEY = 'theme:new-customer-coupon-teaser-hidden';
  const STORAGE_COUPONS_KEY = 'theme:new-customer-coupon-data';
  const DEFAULT_TEASER_HIDE_HOURS = 24;

  function getTeaserHideHoursFromEl(el) {
    const raw = el?.dataset?.teaserHideHours;
    const hours = parseInt(raw || String(DEFAULT_TEASER_HIDE_HOURS), 10);
    if (!Number.isFinite(hours) || hours < 1) return DEFAULT_TEASER_HIDE_HOURS;
    return Math.min(hours, 72);
  }

  function readTeaserHiddenUntil() {
    const raw = localStorage.getItem(STORAGE_TEASER_HIDDEN_KEY);
    if (!raw) return 0;
    // Legacy permanent hide → clear so new cooldown rules apply
    if (raw === 'true') {
      localStorage.removeItem(STORAGE_TEASER_HIDDEN_KEY);
      return 0;
    }
    const until = parseInt(raw, 10);
    return Number.isFinite(until) ? until : 0;
  }

  function isTeaserInCooldown() {
    const until = readTeaserHiddenUntil();
    if (!until) return false;
    if (Date.now() >= until) {
      localStorage.removeItem(STORAGE_TEASER_HIDDEN_KEY);
      return false;
    }
    return true;
  }

  function startTeaserCooldown(hours) {
    const ms = Math.max(1, hours) * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_TEASER_HIDDEN_KEY, String(Date.now() + ms));
  }

  class NewCustomerCouponPopup extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-ncc-overlay]');
      this.backdrop = this.querySelector('[data-ncc-backdrop]');
      this.closeBtn = this.querySelector('[data-ncc-close]');
      this.form = this.querySelector('[data-ncc-form]');
      this.emailInput = this.querySelector('[data-ncc-email]');
      this.submitBtn = this.querySelector('[data-ncc-submit]');
      this.errorEl = this.querySelector('[data-ncc-error]');
      this.couponsEl = this.querySelector('[data-ncc-coupons]');
      this.couponGrid = this.querySelector('[data-ncc-coupon-grid]');
      this.offersEl = this.querySelector('[data-ncc-offers]');

      this.apiEndpoint = this.dataset.apiEndpoint || 'https://ulefone-coupon-check.rosalinchen163.workers.dev';
      this.currencyPrefix = this.dataset.currencyPrefix || '';
      this.errorMessage = this.dataset.errorMessage || 'Sorry, you are not a new customer.';
      this.loggedInOrderCount = this.dataset.loggedInOrderCount;
      this.customerEmail = this.dataset.customerEmail || '';
      this.couponLimit = parseInt(this.dataset.couponLimit || '2', 10);
      this.showTeaser = this.hasAttribute('data-show-teaser');
      this.teaserHideHours = getTeaserHideHoursFromEl(this);

      const couponsScript = this.querySelector('[data-ncc-coupons-json]');
      try {
        this.coupons = JSON.parse(couponsScript?.textContent || '[]');
      } catch {
        this.coupons = [];
      }

      this.coupons.sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));
      this.renderOffersPreview();
      this.ensureCouponsLoaded();
      this.syncDockTeaserLabel();

      this.closeBtn?.addEventListener('click', () => this.hide(true));
      this.backdrop?.addEventListener('click', () => this.hide(true));
      this.form?.addEventListener('submit', (e) => this.handleSubmit(e));

      document.addEventListener('keydown', this.handleKeydown = (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('is-open')) {
          this.hide(true);
        }
      });

      document.addEventListener('ulefone:member-teaser-closed', () => {
        startTeaserCooldown(this.teaserHideHours);
        this.emitTeaserVisibility(false);
      });

      // Returning customers with orders: never show popup / teaser
      if (this.isReturningCustomer()) return;

      if (this.isDesignMode()) return;

      // Already claimed: keep teaser, reopen shows coupon codes
      if (this.hasClaimed()) {
        this.prepareClaimedView();
        if (this.shouldShowTeaser()) {
          this.showTeaserTab();
        }
        return;
      }

      if (this.shouldShowTeaser()) {
        this.showTeaserTab();
      }

      if (this.shouldAutoShow()) {
        const delay = parseInt(this.dataset.delay || '0', 10) * 1000;
        setTimeout(() => this.show(), delay);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    isDesignMode() {
      return (
        window.Shopify?.designMode === true ||
        document.documentElement.classList.contains('shopify-design-mode')
      );
    }

    hasClaimed() {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }

    isReturningCustomer() {
      return (
        this.loggedInOrderCount !== '' &&
        parseInt(this.loggedInOrderCount, 10) > 0
      );
    }

    /** Still able to claim (not claimed, not returning buyer) */
    isEligible() {
      if (this.hasClaimed()) return false;
      if (this.isReturningCustomer()) return false;
      return true;
    }

    shouldAutoShow() {
      if (!this.isEligible()) return false;
      if (this.hasAttribute('only-once') && localStorage.getItem(STORAGE_DISMISSED_KEY) === 'true') {
        return false;
      }
      return true;
    }

    shouldShowTeaser() {
      if (!this.showTeaser) return false;
      if (this.isReturningCustomer()) return false;
      if (isTeaserInCooldown()) return false;
      // After dismiss OR after successful claim
      if (this.hasClaimed()) return true;
      if (localStorage.getItem(STORAGE_DISMISSED_KEY) === 'true') return true;
      return false;
    }

    show() {
      this.hideError();

      if (this.hasClaimed()) {
        this.prepareClaimedView();
      } else {
        this.form?.classList.remove('is-hidden');
        this.couponsEl?.classList.remove('is-visible');
        this.ensureCouponsLoaded().then(() => this.renderOffersPreview());
      }

      this.overlay?.classList.add('is-open');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('new-customer-coupon-popup-open');
      this.hideTeaserTab();
      if (this.hasAttribute('only-once')) {
        localStorage.setItem(STORAGE_DISMISSED_KEY, 'true');
      }
    }

    hide(dismissed) {
      this.overlay?.classList.remove('is-open');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('new-customer-coupon-popup-open');

      if (
        dismissed &&
        this.showTeaser &&
        !this.isReturningCustomer() &&
        !isTeaserInCooldown()
      ) {
        this.showTeaserTab();
      }
    }

    emitTeaserVisibility(visible) {
      document.dispatchEvent(
        new CustomEvent('ulefone:member-teaser-visibility', {
          detail: { visible: !!visible },
        })
      );
    }

    syncDockTeaserLabel() {
      const label = (this.dataset.teaserLabel || '').trim();
      if (!label) return;
      const btn = document.querySelector('#ulefone-floating-dock [data-dock-action="member"]');
      if (btn) btn.setAttribute('aria-label', label);
    }

    showTeaserTab() {
      if (!this.showTeaser) return;
      this.emitTeaserVisibility(true);
      // Re-emit in case floating-dock listened after the first event.
      setTimeout(() => this.emitTeaserVisibility(true), 0);
      setTimeout(() => this.emitTeaserVisibility(true), 300);
    }

    hideTeaserTab() {
      this.emitTeaserVisibility(false);
    }

    openFromTeaser() {
      this.show();
    }

    handleTeaserClose() {
      startTeaserCooldown(this.teaserHideHours);
      this.emitTeaserVisibility(false);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
    }

    hideError() {
      this.errorEl?.classList.remove('is-visible');
    }

    setLoading(loading) {
      if (this.submitBtn) {
        this.submitBtn.disabled = loading;
        this.submitBtn.textContent = loading
          ? (this.submitBtn.dataset.loadingText || 'Checking...')
          : (this.submitBtn.dataset.defaultText || 'Claim Now');
      }
    }

    isLoggedInNewCustomer() {
      return (
        this.loggedInOrderCount !== '' &&
        parseInt(this.loggedInOrderCount, 10) === 0
      );
    }

    loadStoredCoupons() {
      try {
        const raw = localStorage.getItem(STORAGE_COUPONS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed : null;
      } catch {
        return null;
      }
    }

    persistClaimedCoupons() {
      try {
        const payload = this.coupons.slice(0, this.couponLimit);
        localStorage.setItem(STORAGE_COUPONS_KEY, JSON.stringify(payload));
      } catch {
        // ignore quota / private mode errors
      }
    }

    prepareClaimedView() {
      const stored = this.loadStoredCoupons();
      if (stored) {
        this.coupons = stored;
      }

      if (!this.coupons?.length) {
        this.ensureCouponsLoaded().then(() => {
          if (this.coupons?.length) {
            this.persistClaimedCoupons();
            this.renderCoupons();
            this.hideError();
          }
        });
      }

      if (this.coupons?.length) {
        this.renderCoupons();
        this.hideError();
      }

      this.offersEl?.classList.add('is-hidden');
    }

    async ensureCouponsLoaded() {
      if (this.coupons?.length) return this.coupons;
      if (this._couponsLoadPromise) return this._couponsLoadPromise;

      this._couponsLoadPromise = (async () => {
        try {
          const response = await fetch(this.apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'list-coupons' }),
          });
          const data = await response.json().catch(() => ({}));
          if (Array.isArray(data.coupons) && data.coupons.length) {
            this.coupons = data.coupons.sort(
              (a, b) => (a.sortOrder || 999) - (b.sortOrder || 999),
            );
            this.renderOffersPreview();
          }
        } catch (error) {
          console.error('[new-customer-coupon-popup] load coupons failed:', error);
        }
        return this.coupons;
      })();

      return this._couponsLoadPromise;
    }

    completeClaim() {
      this.renderCoupons();
      localStorage.setItem(STORAGE_KEY, 'true');
      this.persistClaimedCoupons();
      this.offersEl?.classList.add('is-hidden');
      // Keep teaser available — it reappears when the popup is closed
    }

    formatOfferLine(coupon) {
      const amount = this.formatAmount(coupon.discountAmount);
      const minimum = this.formatMinimum(coupon.minimumSpend);
      return [amount, minimum].filter(Boolean).join(' ');
    }

    renderOffersPreview() {
      if (!this.offersEl) return;
      if (this.hasClaimed()) {
        this.offersEl.classList.add('is-hidden');
        return;
      }

      const selected = this.coupons.slice(0, this.couponLimit).filter((coupon) => {
        return coupon.discountAmount || coupon.minimumSpend;
      });

      if (!selected.length) {
        if (!this.offersEl.children.length) {
          this.offersEl.hidden = true;
        }
        return;
      }

      this.offersEl.hidden = false;
      this.offersEl.classList.remove('is-hidden');
      this.offersEl.innerHTML = selected
        .map((coupon) => {
          const amount = this.escapeHtml(this.formatAmount(coupon.discountAmount));
          const minimum = this.escapeHtml(this.formatMinimum(coupon.minimumSpend));
          if (!amount && !minimum) return '';
          return `
            <div class="new-customer-coupon-popup__offer">
              ${amount ? `<span class="new-customer-coupon-popup__offer-amount">${amount}</span>` : ''}
              ${minimum ? `<span class="new-customer-coupon-popup__offer-minimum">${minimum}</span>` : ''}
            </div>
          `;
        })
        .join('');
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.hideError();

      const email = this.emailInput?.value?.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      if (this.isLoggedInNewCustomer()) {
        this.completeClaim();
        return;
      }

      this.setLoading(true);

      try {
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          this.showError(
            data.error ||
              'Unable to verify your email right now. Please try again later.'
          );
          return;
        }

        if (data.eligible === false) {
          this.showError(data.error || this.errorMessage);
          return;
        }

        if (Array.isArray(data.coupons) && data.coupons.length) {
          this.coupons = data.coupons;
        }

        if (!this.coupons?.length) {
          this.showError(
            data.error || 'No coupons available at the moment.'
          );
          return;
        }

        this.completeClaim();
      } catch (error) {
        console.error('[new-customer-coupon-popup] claim failed:', error);
        this.showError(
          'Unable to reach the coupon service. Please check your connection and try again.'
        );
      } finally {
        this.setLoading(false);
      }
    }

    formatAmount(amount) {
      const value = String(amount || '').trim();
      if (!value) return '';
      if (/[%$€£¥]/.test(value)) return value;
      if (/^\d+(\.\d+)?$/.test(value)) {
        return `${this.currencyPrefix}${value} OFF`;
      }
      return value;
    }

    formatMinimum(minimum) {
      const value = String(minimum || '').trim();
      if (!value) return '';
      if (/[%$€£¥]/.test(value)) return `over ${value}`;
      if (/^\d+(\.\d+)?$/.test(value)) {
        return `over ${this.currencyPrefix}${value}`;
      }
      return value;
    }

    renderProductScope(coupon) {
      const label = this.escapeHtml(coupon.productScope || '');
      if (!label) return '';

      const url = String(coupon.productScopeUrl || '').trim();
      if (url && /^https?:\/\//i.test(url)) {
        return `<p><strong>Products:</strong> <a class="new-customer-coupon-popup__product-link" href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a></p>`;
      }

      return `<p><strong>Products:</strong> ${label}</p>`;
    }

    renderCoupons() {
      const selected = this.coupons.slice(0, this.couponLimit);
      if (!selected.length) {
        this.showError('No coupons available at the moment.');
        return;
      }

      const copyIcon = `<svg class="new-customer-coupon-popup__copy-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
      const checkIcon = `<svg class="new-customer-coupon-popup__copy-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>`;

      this.form?.classList.add('is-hidden');
      this.couponGrid.innerHTML = selected.map((coupon) => `
        <div class="new-customer-coupon-popup__coupon-card">
          <div class="new-customer-coupon-popup__coupon-amount">${this.escapeHtml(this.formatAmount(coupon.discountAmount))}</div>
          <div class="new-customer-coupon-popup__coupon-minimum">${this.escapeHtml(this.formatMinimum(coupon.minimumSpend))}</div>
          <div class="new-customer-coupon-popup__coupon-code">
            <span>${this.escapeHtml(coupon.code)}</span>
            <button type="button" class="new-customer-coupon-popup__coupon-copy" data-copy-code="${this.escapeHtml(coupon.code)}" aria-label="Copy code">
              ${copyIcon}${checkIcon}
            </button>
          </div>
          <div class="new-customer-coupon-popup__coupon-meta">
            ${this.renderProductScope(coupon)}
            ${coupon.customerScope ? `<p><strong>Eligible:</strong> ${this.escapeHtml(coupon.customerScope)}</p>` : ''}
          </div>
        </div>
      `).join('');

      this.couponGrid.querySelectorAll('[data-copy-code]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const code = btn.dataset.copyCode;
          try {
            await navigator.clipboard?.writeText(code);
          } catch {
            // Fallback for older browsers / blocked clipboard
            const input = document.createElement('input');
            input.value = code;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
          }

          btn.classList.add('is-copied');
          btn.setAttribute('aria-label', 'Copied!');
          clearTimeout(btn._copyTimer);
          btn._copyTimer = setTimeout(() => {
            btn.classList.remove('is-copied');
            btn.setAttribute('aria-label', 'Copy code');
          }, 2000);
        });
      });

      this.couponsEl?.classList.add('is-visible');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }
  }

  if (!customElements.get('new-customer-coupon-popup')) {
    customElements.define('new-customer-coupon-popup', NewCustomerCouponPopup);
  }

  function getPopupInstance() {
    return document.querySelector('new-customer-coupon-popup');
  }

  function openNewMemberBonusPopup() {
    const popup = getPopupInstance();
    if (!popup) {
      console.warn('[new-customer-coupon-popup] instance not found on page');
      return false;
    }
    if (typeof popup.show === 'function') {
      popup.show();
      return true;
    }
    return false;
  }

  window.UlefoneNewMemberBonus = {
    open: openNewMemberBonusPopup,
    getInstance: getPopupInstance,
    isTeaserInCooldown: isTeaserInCooldown,
    startTeaserCooldown: function (hours) {
      const popup = getPopupInstance();
      const resolved =
        typeof hours === 'number' && hours > 0
          ? hours
          : getTeaserHideHoursFromEl(popup);
      startTeaserCooldown(resolved);
    },
    getTeaserHideHours: function () {
      return getTeaserHideHoursFromEl(getPopupInstance());
    },
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-open-new-member-bonus]');
    if (!trigger) return;
    event.preventDefault();
    openNewMemberBonusPopup();
  });
})();
